'use client'

import 'keen-slider/keen-slider.min.css'

import cn from 'classnames'
import { useKeenSlider } from 'keen-slider/react'
import { useEffect, useRef, useState } from 'react'

import { Image } from '@/components/Image'

interface DualImageSwiperProps {
  leftImages: {
    src: string
    alt: string
    width: number
    height: number
  }[]
  rightImages: {
    src: string
    alt: string
    width: number
    height: number
  }[]
  className?: string
}

export function DualImageSwiper({
  leftImages,
  rightImages,
  className = '',
}: DualImageSwiperProps) {
  const [loaded, setLoaded] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Left slider configuration (top to bottom)
  const [leftSliderRef, leftInstanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    loop: true,
    drag: false,
    vertical: true,
    created() {
      setLoaded(true)
    },
  })

  // Right slider configuration (bottom to top - reversed)
  const [rightSliderRef, rightInstanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    loop: true,
    drag: false,
    vertical: true,
  })

  // Visibility observer
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0)
      },
      { threshold: 0.1 },
    )

    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  // Autoplay functionality
  useEffect(() => {
    if (!loaded || !isVisible) return

    const interval = setInterval(() => {
      const left = leftInstanceRef.current
      const right = rightInstanceRef.current
      if (!left || !right) return
      const easing = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t)

      left.moveToIdx(left.track.details.abs + 1, true, {
        duration: 1500,
        easing,
      })
      right.moveToIdx(right.track.details.abs + 1, true, {
        duration: 1500,
        easing,
      })
    }, 3000)

    return () => clearInterval(interval)
  }, [loaded, isVisible, leftInstanceRef, rightInstanceRef])

  return (
    <div
      ref={containerRef}
      className={cn('transition-colors duration-500', {
        'bg-primary': !loaded,
        'bg-transparent': loaded,
      })}
    >
      <div
        className={cn('grid grid-cols-2 transition-opacity duration-500', {
          'opacity-0': !loaded,
          'opacity-100': loaded,
        })}
      >
        {/* Left Slider (Tokyo) - Top to Bottom */}
        <div
          ref={leftSliderRef}
          className="keen-slider relative aspect-square size-full"
        >
          {leftImages.map((image, idx) => (
            <div key={idx} className="keen-slider__slide">
              <Image
                src={image.src}
                alt={image.alt}
                width={image.width}
                height={image.height}
                lazy={false}
                className="block size-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Right Slider (Nagoya) - Bottom to Top */}
        <div
          ref={rightSliderRef}
          className="keen-slider relative aspect-square size-full rotate-180"
        >
          {rightImages.map((image, idx) => (
            <div key={idx} className="keen-slider__slide">
              <Image
                src={image.src}
                alt={image.alt}
                width={image.width}
                height={image.height}
                lazy={false}
                className="block size-full rotate-180 object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
